import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useAuth } from '../../auth/AuthContext';
import {
  CutoffAnalysis,
  DailyBucket,
  GapCandidate,
  InventoryReport,
  InventorySubtotal,
  TenantInventoryReport,
  TwoBucketCutoffs,
  TwoBucketStats,
  YearEndInventoryReport,
  YearEndTenantReport,
  getInventoryReport,
  getInventoryYearEndReport,
} from '../../api';
import Price from '../widgets/Price';

// Scopes the report supports. "current" means right-now on-hand stock;
// year scopes (e.g. "2025") render a year-end snapshot — for tenants
// whose snapshot logic isn't finalized yet the backend returns the
// cutoff-discovery analysis instead of totals.
type Scope = { kind: 'current' } | { kind: 'year'; year: string };
const YEAR_OPTIONS = ['2025'] as const;

function parseScope(yearParam: string | undefined): Scope {
  if (yearParam && (YEAR_OPTIONS as readonly string[]).includes(yearParam)) {
    return { kind: 'year', year: yearParam };
  }
  return { kind: 'current' };
}

function scopeTitle(scope: Scope): string {
  return scope.kind === 'current' ? 'Inventory' : `Inventory — Year End ${scope.year}`;
}

function scopeDescription(scope: Scope): string {
  if (scope.kind === 'current') {
    return (
      'Cost and retail value of on-hand stock right now, by tenant. ' +
      'Values are pre-tax. Items missing a cost are estimated at half their price.'
    );
  }
  return (
    `Closing inventory snapshot for ${scope.year}. Tenants with a finished ` +
    'snapshot show totals; tenants still being tuned show the ' +
    'cutoff-discovery analysis. Values are pre-tax.'
  );
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'current'; report: InventoryReport }
  | { kind: 'year-end'; report: YearEndInventoryReport };

const InventoryReportPage = () => {
  const { idToken } = useAuth();
  const { year } = useParams<{ year?: string }>();
  const location = useLocation();
  const history = useHistory();
  const scope = parseScope(year);

  // URL drives the cutoffs — empty when not present (server uses defaults).
  const urlCutoff1 = new URLSearchParams(location.search).get('cutoff1') ?? '';
  const urlCutoff2 = new URLSearchParams(location.search).get('cutoff2') ?? '';

  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    const fetcher: Promise<LoadState> =
      scope.kind === 'current'
        ? getInventoryReport(idToken).then((report) => ({ kind: 'current', report } as const))
        : getInventoryYearEndReport(idToken, scope.year, {
            cutoff1: urlCutoff1 || undefined,
            cutoff2: urlCutoff2 || undefined,
          }).then((report) => ({ kind: 'year-end', report } as const));
    fetcher
      .then((next) => {
        if (!cancelled) setState(next);
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: (err as Error)?.message || 'failed to load inventory report',
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // scope is recomputed each render from `year`; depending on `year`
    // is what actually drives refetching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, year, urlCutoff1, urlCutoff2]);

  const updateCutoffs = (next: TwoBucketCutoffs) => {
    const params = new URLSearchParams(location.search);
    if (next.cutoff1) params.set('cutoff1', next.cutoff1);
    else params.delete('cutoff1');
    if (next.cutoff2) params.set('cutoff2', next.cutoff2);
    else params.delete('cutoff2');
    const qs = params.toString();
    history.replace({
      pathname: location.pathname,
      search: qs ? `?${qs}` : '',
    });
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 0.5 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937' }}>
          {scopeTitle(scope)}
        </Typography>
        <ScopeSwitcher scope={scope} />
      </Stack>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
        {scopeDescription(scope)}
      </Typography>

      {state.kind === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {state.kind === 'error' && (
        <Alert severity="error">{state.message}</Alert>
      )}

      {state.kind === 'current' && (
        <Stack spacing={4}>
          {state.report.tenants.map((tenant) => (
            <TenantSection key={tenant.slug} tenant={tenant} />
          ))}
        </Stack>
      )}

      {state.kind === 'year-end' && (
        <Stack spacing={4}>
          {state.report.tenants.map((tenant) => (
            <YearEndTenantBlock
              key={tenant.slug}
              tenant={tenant}
              onCutoffsChange={updateCutoffs}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const ScopeSwitcher = ({ scope }: { scope: Scope }) => {
  const value = scope.kind === 'current' ? 'current' : scope.year;
  return (
    <ToggleButtonGroup size="small" exclusive value={value}>
      <ToggleButton value="current" component={RouterLink} to="/reports/inventory">
        Current
      </ToggleButton>
      {YEAR_OPTIONS.map((y) => (
        <ToggleButton
          key={y}
          value={y}
          component={RouterLink}
          to={`/reports/inventory/${y}`}
        >
          Year End {y}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

const YearEndTenantBlock = ({
  tenant,
  onCutoffsChange,
}: {
  tenant: YearEndTenantReport;
  onCutoffsChange: (next: TwoBucketCutoffs) => void;
}) => {
  if (tenant.report) {
    // Two-bucket tenants (currently just backroom) get extra header
    // furniture: date inputs that drive URL query params + a bucket
    // attribution row so we can see whether the cutoffs are pulling
    // their weight.
    const extras =
      tenant.active_cutoffs && tenant.bucket_stats ? (
        <Box sx={{ mb: 2 }}>
          <TwoBucketControls
            cutoffs={tenant.active_cutoffs}
            onChange={onCutoffsChange}
          />
          <BucketStatsRow stats={tenant.bucket_stats} sx={{ mt: 1.5 }} />
        </Box>
      ) : null;
    return <TenantSection tenant={tenant.report} extras={extras} />;
  }
  if (tenant.cutoff_analysis) {
    return (
      <CutoffAnalysisSection label={tenant.label} analysis={tenant.cutoff_analysis} />
    );
  }
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
        {tenant.label}
      </Typography>
      <Alert severity="warning" sx={{ mt: 1 }}>
        No year-end strategy configured for this tenant.
      </Alert>
    </Paper>
  );
};

const TwoBucketControls = ({
  cutoffs,
  onChange,
}: {
  cutoffs: TwoBucketCutoffs;
  onChange: (next: TwoBucketCutoffs) => void;
}) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={2}
    alignItems={{ xs: 'stretch', sm: 'flex-end' }}
  >
    <TextField
      label="Cutoff 1 (latest reset wins)"
      type="date"
      size="small"
      value={cutoffs.cutoff1}
      onChange={(e) => onChange({ ...cutoffs, cutoff1: e.target.value })}
      InputLabelProps={{ shrink: true }}
      helperText="Latest reset in (zero, cutoff 1] wins"
    />
    <TextField
      label="Cutoff 2 (untouched fallback)"
      type="date"
      size="small"
      value={cutoffs.cutoff2}
      onChange={(e) => onChange({ ...cutoffs, cutoff2: e.target.value })}
      InputLabelProps={{ shrink: true }}
      helperText="First post-zero event is a reset, by cutoff 2"
    />
  </Stack>
);

const BucketStatsRow = ({
  stats,
  sx,
}: {
  stats: TwoBucketStats;
  sx?: object;
}) => (
  <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap" sx={sx}>
    <BucketStat label="Bucket 1" value={stats.items_via_bucket_1} />
    <BucketStat label="Bucket 2" value={stats.items_via_bucket_2} />
    <BucketStat label="Uncounted" value={stats.items_uncounted} muted />
  </Stack>
);

const BucketStat = ({
  label,
  value,
  muted,
}: {
  label: string;
  value: number;
  muted?: boolean;
}) => (
  <Box>
    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
      {label}
    </Typography>
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 600, color: muted ? '#94a3b8' : '#1f2937' }}
    >
      {value.toLocaleString()}
    </Typography>
  </Box>
);

const TenantSection = ({
  tenant,
  extras,
}: {
  tenant: TenantInventoryReport;
  extras?: React.ReactNode;
}) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
      {tenant.label}
    </Typography>
    {extras}
    <Counts tenant={tenant} />
    <Divider sx={{ my: 2 }} />
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Band</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Units</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Total Cost</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Total Price</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <SubtotalRow
            label="Estimate"
            subtotal={tenant.estimate}
            hint="cost inferred as price ÷ 2"
          />
          <SubtotalRow
            label="Absolute"
            subtotal={tenant.absolute}
            hint="items with an explicit cost"
          />
          <SubtotalRow label="Total" subtotal={tenant.total} bold />
        </TableBody>
      </Table>
    </TableContainer>
    {tenant.slug === 'thequeensflorist' && tenant.absolute.unit_count > 0 && (
      <Box
        sx={{
          mt: 2,
          pt: 2,
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'baseline',
          gap: 1,
          color: '#64748b',
          fontSize: '0.875rem',
        }}
      >
        <span>Average cost per unit (priced items):</span>
        <Typography
          component="span"
          sx={{ color: '#1f2937', fontWeight: 600, fontSize: '0.875rem' }}
        >
          <Price
            price={Math.round(tenant.absolute.cost_cents / tenant.absolute.unit_count)}
            showZero
          />
        </Typography>
      </Box>
    )}
  </Paper>
);

const Counts = ({ tenant }: { tenant: TenantInventoryReport }) => {
  const parts: string[] = [`${tenant.item_count.toLocaleString()} items`];
  if (tenant.missing_cost_count > 0) {
    parts.push(`${tenant.missing_cost_count.toLocaleString()} missing cost`);
  }
  if (tenant.missing_price_count > 0) {
    parts.push(`${tenant.missing_price_count.toLocaleString()} missing price`);
  }
  return (
    <Typography variant="body2" sx={{ color: '#64748b' }}>
      {parts.join(' · ')}
    </Typography>
  );
};

type SubtotalRowProps = {
  label: string;
  subtotal: InventorySubtotal;
  hint?: string;
  bold?: boolean;
};

const SubtotalRow = ({ label, subtotal, hint, bold }: SubtotalRowProps) => {
  const cellSx = bold ? { fontWeight: 700 } : undefined;
  return (
    <TableRow>
      <TableCell sx={cellSx}>
        {label}
        {hint && (
          <Typography
            component="span"
            variant="caption"
            sx={{ color: '#94a3b8', ml: 1 }}
          >
            {hint}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        {subtotal.unit_count.toLocaleString()}
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        <Price price={subtotal.cost_cents} showZero />
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        <Price price={subtotal.price_cents} showZero />
      </TableCell>
    </TableRow>
  );
};

const CutoffAnalysisSection = ({
  label,
  analysis,
}: {
  label: string;
  analysis: CutoffAnalysis;
}) => {
  const hasAny = analysis.longest_gap !== null;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
        Cutoff discovery — for each item, gap = (first reset after the
        bulk zero on {analysis.zero_event_date}) − zero. Percentiles show
        how tightly items cluster: if P95 lands well before the max,
        a small tail of late counts is dragging the cutoff out.
      </Typography>

      <Stack
        direction="row"
        spacing={4}
        useFlexGap
        flexWrap="wrap"
        sx={{ color: '#1f2937', mb: 2 }}
      >
        <Stat label="Items scanned" value={analysis.items_scanned.toLocaleString()} />
        <Stat label="Zeroed in window" value={analysis.items_with_zero.toLocaleString()} />
        <Stat
          label="With reset after zero"
          value={analysis.items_with_reset_after_zero.toLocaleString()}
        />
      </Stack>

      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 2 }}>
        Search window (UTC): {formatTs(analysis.window_start)} →{' '}
        {formatTs(analysis.window_end)}
      </Typography>

      {hasAny ? (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            <PercentileCard
              label="Max (P100)"
              sublabel="proposed cutoff"
              candidate={analysis.longest_gap}
              emphasize
            />
            <PercentileCard label="P99" candidate={analysis.p99} />
            <PercentileCard label="P95" candidate={analysis.p95} />
          </Box>
          <DailyHistogram buckets={analysis.daily_histogram} />
        </>
      ) : (
        <Alert severity="warning">
          No items had a reset event after a zero in the search window.
          Either the window is wrong or the events look different from
          what we expect.
        </Alert>
      )}
    </Paper>
  );
};

const DailyHistogram = ({ buckets }: { buckets: DailyBucket[] }) => {
  if (buckets.length === 0) return null;
  const max = Math.max(...buckets.map((b) => b.items));
  const BAR_WIDTH = 40;
  const lines = buckets.map((b) => {
    const filled = max > 0 ? Math.round((b.items / max) * BAR_WIDTH) : 0;
    const bar = '█'.repeat(filled);
    return `${b.date}  ${bar.padEnd(BAR_WIDTH)} ${b.items}`;
  });
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', mb: 1 }}>
        Items first-reset per UTC date
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: 1,
          fontSize: '0.8125rem',
          lineHeight: 1.4,
          overflowX: 'auto',
        }}
      >
        {lines.join('\n')}
      </Box>
    </Box>
  );
};

const PercentileCard = ({
  label,
  sublabel,
  candidate,
  emphasize,
}: {
  label: string;
  sublabel?: string;
  candidate: GapCandidate | null;
  emphasize?: boolean;
}) => (
  <Box
    sx={{
      border: '1px solid #e5e7eb',
      borderRadius: 1,
      p: 2,
      backgroundColor: emphasize ? '#f9fafb' : '#ffffff',
    }}
  >
    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f2937' }}>
        {label}
      </Typography>
      {sublabel && (
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
          {sublabel}
        </Typography>
      )}
    </Stack>
    {candidate ? (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          columnGap: 1.5,
          rowGap: 0.5,
          fontSize: '0.875rem',
        }}
      >
        <Typography component="span" variant="body2" sx={{ color: '#64748b' }}>Cutoff</Typography>
        <Typography component="span" variant="body2" sx={{ color: '#1f2937', fontWeight: 600 }}>
          {formatTs(candidate.reset_at)}
        </Typography>
        <Typography component="span" variant="body2" sx={{ color: '#64748b' }}>Gap</Typography>
        <Typography component="span" variant="body2" sx={{ color: '#1f2937' }}>
          {formatGap(candidate.gap_seconds)}
        </Typography>
        <Typography component="span" variant="body2" sx={{ color: '#64748b' }}>Item</Typography>
        <Typography component="span" variant="body2" sx={{ color: '#1f2937' }}>
          {candidate.item_name}{' '}
          <Typography component="span" variant="caption" sx={{ color: '#94a3b8' }}>
            ({candidate.item_id})
          </Typography>
        </Typography>
        <Typography component="span" variant="body2" sx={{ color: '#64748b' }}>Zeroed</Typography>
        <Typography component="span" variant="body2" sx={{ color: '#1f2937' }}>
          {formatTs(candidate.zeroed_at)}
        </Typography>
      </Box>
    ) : (
      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
        n/a
      </Typography>
    )}
  </Box>
);

const Stat = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

function formatTs(rfc3339: string): string {
  const d = new Date(rfc3339);
  if (Number.isNaN(d.getTime())) return rfc3339;
  return d.toLocaleString();
}

function formatGap(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  if (hours < 48) return remMin === 0 ? `${hours}h` : `${hours}h ${remMin}m`;
  const days = Math.floor(hours / 24);
  const remHr = hours % 24;
  return remHr === 0 ? `${days}d` : `${days}d ${remHr}h`;
}

export default InventoryReportPage;
